<template>
  <el-scrollbar class="main-container" viewClass="main-container-scrollbar-view"
                wrap-class="main-container-scrollbar-wrapper">
    <div class="monitor-container">
      <!-- 顶部状态栏 -->
      <div class="header-section">
        <div class="title-group">
          <i class="el-icon-odometer"></i>
          <span class="main-title">{{ title }}</span>
        </div>
        <div class="info-group">
          <el-date-picker
            size="mini"
            v-model="picker_date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            type="date"
            placeholder="选择日期">
          </el-date-picker>
          <el-time-select
            size="mini"
            v-model="picker_time"
            :picker-options="{start: '00:00',step: '00:30',end: '23:30'}"
            placeholder="选择时间">
          </el-time-select>
          <el-button style="margin-left: 20px;" size="mini" @click="refreshAll">查询</el-button>
          <span class="update-label" style="margin-left: 20px;">数据最后更新:</span>
          <span class="update-time">{{ lastUpdateTime || '--:--:--' }}</span>
        </div>
      </div>

      <!-- 关键指标 KPI 卡片 -->
      <div class="kpi-flex-container">
        <el-card v-for="(item, index) in kpiConfig" :key="index"
                 shadow="never" class="kpi-card-item"
                 :style="{ borderLeftColor: item.color }">
          <div class="kpi-content">
            <div class="kpi-info">
              <div class="label">
                <span>{{ item.name }}</span>
                <span v-if="item.sub_name" class="sub-name">{{ item.sub_name }}</span>
              </div>
              <div class="value">
                {{ item.value !== null ? item.value : '0' }}<small>{{ item.unit }}</small>
              </div>
            </div>
            <i :class="[item.icon, 'kpi-icon']" :style="{ color: item.color }"></i>
          </div>
        </el-card>
        <el-card shadow="never" class="kpi-card-item" style="border-left-color: #3486EF; cursor: pointer;">
          <div class="kpi-content" @click="showVideoLogs">
            <div class="kpi-info">
              <div class="label">监控视频</div>
              <div class="value">{{ metrics.video_logs.length }}<small>个</small></div>
            </div>
            <i class="el-icon-video-camera-solid kpi-icon" style="color: #3486EF;"></i>
          </div>
        </el-card>
      </div>

      <!-- 地理位置信息条 -->
      <el-row :gutter="20" class="location-row mt-20">
        <el-col :span="24">
          <el-card shadow="never" class="location-card">
            <div class="location-wrapper">
              <div class="loc-left">
                <i class="el-icon-location-information"></i>
                <span class="loc-label">当前位置：</span>
                <span class="loc-address">{{ metrics.address || '-' }}</span>
                <el-button
                  v-if="metrics.address"
                  type="primary"
                  size="mini"
                  icon="el-icon-map-location"
                  style="margin-left: 20px"
                  @click="openPathDialog">
                  历史轨迹
                </el-button>
              </div>
              <div class="loc-right">
                <span class="loc-label">经纬度：</span>
                <el-tooltip content="点击跳转至高德地图查看" placement="top" v-if="metrics.longitude">
                  <el-link type="primary" :underline="false" class="geo-link" @click="openMap">
                    {{ metrics.longitude }}, {{ metrics.latitude }}
                    <i class="el-icon-position"></i>
                  </el-link>
                </el-tooltip>
                <span v-else>-</span>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 图表展示 -->
      <el-row :gutter="20" class="chart-row mt-20">
        <el-col :span="24">
          <el-card shadow="never" header="电力系统数据">
            <div ref="powerChart" class="chart-box"></div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="chart-row mt-20">
        <el-col :span="24">
          <!-- 标题增加时速说明 -->
          <el-card shadow="never" header="运动与震动趋势 (震动g / 时速km/h)">
            <div ref="vibrationChart" class="chart-box"></div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="chart-row mt-20">
        <el-col :span="24">
          <el-card shadow="never" header="表面温度监测">
            <div ref="tempChart" class="chart-box"></div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="chart-row mt-20" v-if="type === 'bus'">
        <el-col :span="24">
          <el-card shadow="never" header="核心温度监测">
            <div ref="machineTempChart" class="chart-box"></div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="chart-row mt-20" v-if="type === 'car'">
        <el-col :span="24">
          <el-card shadow="never" header="光照强度变化轨迹">
            <div ref="lightChart" class="chart-box-s"></div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 历史轨迹弹窗 -->
    <el-dialog
      title="历史轨迹查询"
      :visible.sync="pathDialogVisible"
      append-to-body
      :close-on-click-modal="false" custom-class="min-w-800 path-dialog"
      @opened="initPathMap">
      <div class="path-toolbar">
        <el-date-picker
          v-model="pathDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="选择日期"
          size="small"
          :picker-options="pickerOptions">
        </el-date-picker>
        <el-button type="primary" size="small" icon="el-icon-search" @click="fetchPathData" :loading="pathLoading"
                   style="margin-left: 10px">
          查询轨迹
        </el-button>
        <span class="path-info" v-if="pathPoints.length">共找到 {{ pathPoints.length }} 个有效轨迹点</span>
      </div>
      <div id="path-map-container" v-loading="pathLoading"></div>
    </el-dialog>
    <el-dialog title="查看视频" :visible.sync="show_video_logs" append-to-body
               :close-on-click-modal="false" custom-class="min-w-800">
      <div class="video-list" v-if="show_video_logs">
        <div class="video-item" v-for="(item, index) in metrics.video_logs" :key="index">
          <video :src="item.video_url" controls v-if="item.video_url.endsWith('mp4')"></video>
          <img :src="item.video_url" alt="" v-else>
          <div class="label">{{ item.video_name }}</div>
        </div>
      </div>
    </el-dialog>
  </el-scrollbar>
</template>

<script>
import * as echarts from 'echarts';
import {sensorLastData, sensorLast30Data, sensorPathData} from "@/api/sensor";

export default {
  name: "SensorResampleMonitor",
  data() {
    return {
      type: 'bus',
      title: '',
      picker_date: null,
      picker_time: null,
      timer: null,
      lastUpdateTime: '',
      metrics: {
        slope: null,
        vibration: null,
        light: null,
        lights: {},
        avgTemp: null,
        avgMachineTemp: null,
        latitude: null,
        longitude: null,
        address: '',
        speed: null,
        voltage: null,
        current: null,
        power: null,
        video_logs: [],
      },
      history: {
        times: [],
        vX: [], vY: [], vZ: [],
        light: [],
        lights: {},
        speed: [], // 新增时速历史
        temps: Array.from({length: 8}, () => []),
        machineTemps: {
          water_pump_temperature: [],
          oxidize_temperature: [],
          t10: [],
          t11: [],
          t23: [],
          t3: [],
          t4: [],
          t5: [],
          t7: [],
        },
        voltage: [],
        current: [],
        power: []
      },
      charts: {vibration: null, temp: null, machineTemp: null, light: null, power: null},
      machine_names: {
        water_pump_temperature: "甲醇水泵温度",
        oxidize_temperature: "氧化传感器温度",
        t10: "T10重整器上",
        t11: "T11重整器下",
        t23: "T23高温重制",
        t3: "T3燃烧器下",
        t4: "T4燃烧器上",
        t5: "T5重整出氢",
        t7: "T7废气出口",
        voltage: "电压",
        current: "电流",
        power: "功率",
      },
      //
      pathDialogVisible: false,
      pathDate: new Date().toISOString().split('T')[0], // 默认今天
      pathLoading: false,
      pathPoints: [],
      pathMap: null,
      pathPolyline: null,
      startEndMarkers: [],
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now(); // 不允许选未来
        }
      },
      show_video_logs: false,
    };
  },
  computed: {
    kpiConfig() {
      let common_config = [
        {name: '实时时速', value: this.metrics.speed, unit: 'km/h', icon: 'el-icon-timer', color: '#673AB7'},
        {name: '电压', value: this.metrics.voltage, unit: 'V', icon: 'el-icon-cpu', color: '#FF9800'},
        {name: '电流', value: this.metrics.current, unit: 'A', icon: 'el-icon-connection', color: '#00BCD4'},
        {name: '功率', value: this.metrics.power, unit: 'W', icon: 'el-icon-lightning', color: '#4CAF50'},
        {name: '设备倾角', value: this.metrics.slope, unit: '°', icon: 'el-icon-stopwatch', color: '#409EFF'},
        {name: '总震动力', value: this.metrics.vibration, unit: 'g', icon: 'el-icon-set-up', color: '#67C23A'},
        {name: '表面平均温度', value: this.metrics.avgTemp, unit: '℃', icon: 'el-icon-discover', color: '#F56C6C'},
      ];
      if (this.type === 'car') {
        for (let device_id in this.metrics.lights) {
          common_config.push({
            name: '环境光照',
            sub_name: device_id,
            value: this.metrics.lights[device_id],
            unit: 'Lux',
            icon: 'el-icon-sunny',
            color: '#E6A23C'
          })
        }
      }
      if (this.type === 'bus') {
        common_config.push({
          name: '核心平均温度',
          value: this.metrics.avgMachineTemp,
          unit: '℃',
          icon: 'el-icon-discover',
          color: '#c54f2b'
        })
      }
      return common_config;
    }
  },
  created() {
    this.type = this.$route.params.type
    if (this.type !== 'bus' || this.type !== 'car') {
      window.close()
    }
    this.title = this.type === 'bus' ? '甲醇燃料电池商用车发电系统监测诊断数据平台' : '一体化光伏穹顶发电情况监测诊断数据平台'
    document.title = this.title
  },
  mounted() {
    this.initCharts();
    this.refreshAll();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    window.removeEventListener('resize', this.handleResize);
    Object.values(this.charts).forEach(c => c && c.dispose());
  },
  methods: {
    initCharts() {
      this.charts.vibration = echarts.init(this.$refs.vibrationChart);
      this.charts.temp = echarts.init(this.$refs.tempChart);
      this.type === 'bus' && (this.charts.machineTemp = echarts.init(this.$refs.machineTempChart));
      this.type === 'car' && (this.charts.light = echarts.init(this.$refs.lightChart));
      this.charts.power = echarts.init(this.$refs.powerChart)
    },

    async refreshAll() {
      // 1. 首先获取初始的 30 条历史记录
      await this.getInitialHistory();

      // 2. 清除旧的定时器，防止重复
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }

      if (this.picker_date && this.picker_time) return

      // 3. 计算距离“下一个分钟的第 20 秒”还有多少毫秒
      const now = new Date();
      const currentSeconds = now.getSeconds();
      const currentMilliseconds = now.getMilliseconds();

      // 目标是每分钟的第 20 秒执行
      const targetSecond = 20;
      let delaySeconds = targetSecond - currentSeconds;

      // 如果当前已经过了 20 秒，则需要等待到下一分钟的第 20 秒
      if (delaySeconds <= 0) {
        delaySeconds += 60;
      }

      // 转换为毫秒，并减去当前的毫秒偏移，保证精确度
      const msToWait = (delaySeconds * 1000) - currentMilliseconds;

      console.log(`系统对齐：将在 ${delaySeconds} 秒后启动轮询，以避开后端同步峰值。`);

      // 4. 使用 setTimeout 进行对齐启动
      setTimeout(() => {
        // 立即执行一次，获取对齐后的最新点
        this.fetchLatest();

        // 然后启动每 60 秒一次的循环
        this.timer = setInterval(this.fetchLatest, 60000);
      }, msToWait);
    },

    openMap() {
      if (!this.metrics.latitude || !this.metrics.longitude) {
        return this.$message.warning("暂无经纬度信息");
      }

      const url = `https://ditu.amap.com/regeo?lng=${this.metrics.longitude}&lat=${this.metrics.latitude}&name=${this.metrics.address}&src=uriapi&callnative=0&innersrc=uriapi`;

      window.open(url, '_blank');
    },

    resampleHistory(res) {
      const result = {
        times: [], vX: [], vY: [], vZ: [], light: [], lights: {}, speed: [], voltage: [], current: [], power: [],
        temps: Array.from({length: 8}, () => []),
        machineTemps: {
          water_pump_temperature: [],
          oxidize_temperature: [],
          t10: [],
          t11: [],
          t23: [],
          t3: [],
          t4: [],
          t5: [],
          t7: [],
        }
      };

      for (let device_id in res.light_logs) {
        result.lights[device_id] = []
      }

      const getAverage = (arr, key) => {
        const validValues = arr
          .map(item => parseFloat(item[key]))
          .filter(val => !isNaN(val) && val !== null);

        if (validValues.length === 0) return null;

        const sum = validValues.reduce((a, b) => a + b, 0);
        return sum / validValues.length;
      };

      const groupByMin = (arr) => {
        const map = {};
        (arr || []).forEach(item => {
          const m = item.created_at.substring(0, 16);
          if (!map[m]) map[m] = [];
          map[m].push(item);
        });
        return map;
      };

      const dMap = groupByMin(res.driving_logs);
      let lMaps = {}
      for (let device_id in res.light_logs) {
        lMaps[device_id] = groupByMin(res.light_logs[device_id]);
      }
      const tMap = groupByMin(res.temperature_logs);
      const mtMap = groupByMin(res.machine_logs);
      // const pMap = groupByMin(res.machine_logs);
      const iMap = groupByMin(res.iot_logs);

      for (let i = 0; i < 30; i++) {
        const k = res.times[i];
        result.times.push(k.split(' ')[1]);

        // --- 震动与时速：如果没有数据，则压入 null 而非 lastV ---
        if (dMap[k]) {
          const avgX = getAverage(dMap[k], 'vibration_x');
          const avgY = getAverage(dMap[k], 'vibration_y');
          const avgZ = getAverage(dMap[k], 'vibration_z');
          const avgS = getAverage(dMap[k], 'speed');

          result.vX.push(avgX !== null ? avgX.toFixed(3) : null);
          result.vY.push(avgY !== null ? avgY.toFixed(3) : null);
          result.vZ.push(avgZ !== null ? avgZ.toFixed(3) : null);
          result.speed.push(avgS !== null ? avgS.toFixed(1) : null);
        } else {
          // 关键修改：存入 null，ECharts 会自动断开曲线
          result.vX.push(null);
          result.vY.push(null);
          result.vZ.push(null);
          result.speed.push(null);
        }

        // 电力处理
        if (iMap[k]) {
          result.voltage.push(getAverage(iMap[k], 'voltage')?.toFixed(2) || null);
          result.current.push(getAverage(iMap[k], 'current')?.toFixed(2) || null);
          result.power.push(getAverage(iMap[k], 'power')?.toFixed(1) || null);
        } else {
          result.voltage.push(null);
          result.current.push(null);
          result.power.push(null);
        }

        // --- 光照 ---
        for (let device_id in res.light_logs) {
          if (lMaps[device_id][k]) {
            const avgL = getAverage(lMaps[device_id][k], 'light');
            result.lights[device_id].push(avgL !== null ? avgL.toFixed(2) : null);
          } else {
            result.lights[device_id].push(null);
          }
        }

        // --- 表面温度 ---
        if (tMap[k]) {
          for (let c = 1; c <= 8; c++) {
            const avgT = getAverage(tMap[k], `ch0${c}`);
            result.temps[c - 1].push(avgT !== null ? avgT.toFixed(2) : null);
          }
        } else {
          for (let c = 1; c <= 8; c++) result.temps[c - 1].push(null);
        }

        // --- 核心温度 ---
        if (mtMap[k]) {
          for (let key in result.machineTemps) {
            const avgT = getAverage(mtMap[k], key);
            result.machineTemps[key].push(avgT !== null ? avgT.toFixed(2) : null);
          }
        } else {
          for (let key in result.machineTemps) {
            result.machineTemps[key].push(null);
          }
        }
      }
      return result;
    },

    async getInitialHistory() {
      try {
        let date = ''
        if (this.picker_date && this.picker_time) {
          date = `${this.picker_date} ${this.picker_time}`
        }
        const res = await sensorLast30Data(this.type, {date: date});
        this.history = this.resampleHistory(res);
        this.metrics.video_logs = res.video_logs;
        // 更新 KPIs
        if (res.driving_logs.length) {
          const lastD = res.driving_logs[res.driving_logs.length - 1];
          this.metrics.speed = lastD.speed;
          this.metrics.slope = lastD.slope;
          this.metrics.vibration = lastD.vibration;
          this.metrics.address = lastD.address;
          this.metrics.latitude = lastD.latitude;
          this.metrics.longitude = lastD.longitude;
        }
        // 更新电力瞬时值
        if (res.iot_logs && res.iot_logs.length) {
          const lastI = res.iot_logs[res.iot_logs.length - 1];
          this.metrics.voltage = lastI.voltage;
          this.metrics.current = lastI.current;
          this.metrics.power = lastI.power;
        }
        for (let device_id in res.light_logs) {
          if (res.light_logs[device_id].length) this.metrics.lights[device_id] = res.light_logs[device_id][res.light_logs[device_id].length - 1].light;
        }
        if (res.temperature_logs.length) this.calculateAvgTemp(res.temperature_logs[res.temperature_logs.length - 1]);
        if (res.machine_logs.length) this.calculateMachineAvgTemp(res.machine_logs[res.machine_logs.length - 1]);

        this.renderAllCharts();
      } catch (e) {
        console.error(e);
      }
    },

    async fetchLatest() {
      try {
        const res = await sensorLastData(this.type);
        const {driving_log, light_logs, temperature_log, machine_log, iot_log, now} = res;
        const timeStr = now.substring(11, 16);

        if (this.history.times[this.history.times.length - 1] === timeStr) return;

        let lights = {}
        for (let device_id in light_logs) {
          lights[device_id] = light_logs[device_id] ? light_logs[device_id].light : null;
        }

        // 更新瞬时值
        this.metrics.voltage = iot_log ? iot_log.voltage : null;
        this.metrics.current = iot_log ? iot_log.current : null;
        this.metrics.power = iot_log ? iot_log.power : null;
        this.metrics.slope = driving_log ? driving_log.slope : null;
        this.metrics.vibration = driving_log ? driving_log.vibration : null;
        this.metrics.lights = lights;
        this.metrics.latitude = driving_log ? driving_log.latitude : null;
        this.metrics.longitude = driving_log ? driving_log.longitude : null;
        this.metrics.address = driving_log ? driving_log.address : null;
        this.metrics.speed = driving_log ? driving_log.speed : null;
        this.lastUpdateTime = now;
        this.calculateAvgTemp(temperature_log ? temperature_log : []);
        this.calculateMachineAvgTemp(machine_log ? machine_log : []);

        // 追加历史数据
        this.history.times.push(timeStr);
        this.history.voltage.push(iot_log ? iot_log.voltage : null);
        this.history.current.push(iot_log ? iot_log.current : null);
        this.history.power.push(iot_log ? iot_log.power : null);
        this.history.vX.push(driving_log ? (driving_log.vibration_x !== null ? parseFloat(driving_log.vibration_x).toFixed(3) : null) : null);
        this.history.vY.push(driving_log ? (driving_log.vibration_y !== null ? parseFloat(driving_log.vibration_y).toFixed(3) : null) : null);
        this.history.vZ.push(driving_log ? (driving_log.vibration_z !== null ? parseFloat(driving_log.vibration_z).toFixed(3) : null) : null);
        this.history.speed.push(driving_log ? (driving_log.speed !== null ? parseFloat(driving_log.speed).toFixed(3) : null) : null);
        for (let device_id in this.history.lights) {
          this.history.lights[device_id].push(lights[device_id] !== null ? parseFloat(lights[device_id]).toFixed(2) : null);
        }
        for (let i = 1; i <= 8; i++) {
          this.history.temps[i - 1].push(temperature_log ? (temperature_log[`ch0${i}`] !== null ? parseFloat(temperature_log[`ch0${i}`]).toFixed(2) : null) : null);
        }
        for (let key in this.history.machineTemps) {
          this.history.machineTemps[key].push(machine_log ? (machine_log[key] !== null ? parseFloat(machine_log[key]).toFixed(2) : null) : null);
        }

        if (this.history.times.length > 30) {
          this.history.times.shift();
          this.history.vX.shift();
          this.history.vY.shift();
          this.history.vZ.shift();
          this.history.speed.shift();
          for (let device_id in this.history.lights) {
            this.history.lights[device_id].shift();
          }
          this.history.temps.forEach(a => a.shift());
        }
        this.renderAllCharts();
      } catch (e) {
        console.error(e);
      }
    },

    calculateAvgTemp(obj) {
      let s = 0;
      for (let i = 1; i <= 8; i++) s += parseFloat(obj[`ch0${i}`]);
      this.metrics.avgTemp = (s / 8).toFixed(2);
    },

    calculateMachineAvgTemp(obj) {
      let s = 0;
      for (let key in this.history.machineTemps) s += parseFloat(obj[key]);
      this.metrics.avgMachineTemp = (s / 8).toFixed(2);
    },

    renderAllCharts() {
      const commonGrid = {top: '15%', left: '3%', right: '4%', bottom: '12%', containLabel: true};

      // 震动图：升级为双 Y 轴
      this.charts.vibration.setOption({
        color: ['#409EFF', '#67C23A', '#E6A23C', '#673AB7'],
        tooltip: {trigger: 'axis'},
        legend: {bottom: 0, icon: 'rect'},
        grid: commonGrid,
        xAxis: {type: 'category', boundaryGap: false, data: this.history.times},
        yAxis: [
          {type: 'value', name: '震动(g)', splitLine: {lineStyle: {type: 'dashed'}}},
          {type: 'value', name: '时速(km/h)', position: 'right', splitLine: {show: false}}
        ],
        series: [
          {name: 'X轴', type: 'line', connectNulls: false, smooth: true, showSymbol: false, data: this.history.vX},
          {name: 'Y轴', type: 'line', connectNulls: false, smooth: true, showSymbol: false, data: this.history.vY},
          {name: 'Z轴', type: 'line', connectNulls: false, smooth: true, showSymbol: false, data: this.history.vZ},
          {
            name: '时速', type: 'line', connectNulls: false, yAxisIndex: 1, smooth: true, showSymbol: false,
            data: this.history.speed, lineStyle: {width: 3}, areaStyle: {opacity: 0.1}
          }
        ]
      });

      this.charts.temp.setOption({
        tooltip: {trigger: 'axis'},
        legend: {type: 'scroll', bottom: 0},
        grid: commonGrid,
        xAxis: {type: 'category', data: this.history.times},
        yAxis: {type: 'value', scale: true},
        series: this.history.temps.map((d, i) => ({
          name: `CH${i + 1}`, type: 'line', connectNulls: false, smooth: true, showSymbol: false, data: d
        }))
      });

      let machine_series = []
      for (let key in this.history.machineTemps) {
        machine_series.push({
          name: this.machine_names[key],
          type: 'line',
          connectNulls: false,
          smooth: true,
          showSymbol: false,
          data: this.history.machineTemps[key]
        })
      }

      this.type === 'bus' && this.charts.machineTemp.setOption({
        tooltip: {trigger: 'axis'},
        legend: {type: 'scroll', bottom: 0},
        grid: commonGrid,
        xAxis: {type: 'category', data: this.history.times},
        yAxis: {type: 'value', scale: true},
        series: machine_series
      });

      if (this.type === 'car') {
        let light_series = []
        for (let device_id in this.history.lights) {
          light_series.push({
            name: device_id,
            type: 'line',
            connectNulls: false,
            smooth: true,
            areaStyle: {opacity: 0.1},
            data: this.history.lights[device_id]
          });
        }
        this.charts.light.setOption({
          color: ['#FF9800', '#00BCD4', '#4CAF50'],
          tooltip: {trigger: 'axis'},
          grid: {top: '20%', left: '3%', right: '3%', bottom: '15%', containLabel: true},
          xAxis: {type: 'category', data: this.history.times},
          yAxis: {type: 'value', name: 'Lux'},
          series: light_series
        });
      }

      this.charts.power.setOption({
        color: ['#FF9800', '#00BCD4', '#4CAF50'],
        tooltip: {trigger: 'axis'},
        legend: {bottom: 0},
        grid: commonGrid,
        xAxis: {type: 'category', boundaryGap: false, data: this.history.times},
        yAxis: [
          {type: 'value', name: '电压/电流', splitLine: {lineStyle: {type: 'dashed'}}},
          {type: 'value', name: '功率(W)', position: 'right', splitLine: {show: false}}
        ],
        series: [
          {name: '电压(V)', type: 'line', smooth: true, showSymbol: false, data: this.history.voltage},
          {name: '电流(A)', type: 'line', smooth: true, showSymbol: false, data: this.history.current},
          {
            name: '功率(W)',
            type: 'line',
            smooth: true,
            showSymbol: false,
            yAxisIndex: 1,
            areaStyle: {opacity: 0.1},
            data: this.history.power
          }
        ]
      });
    },

    handleResize() {
      Object.values(this.charts).forEach(c => c && c.resize());
    },

    // 打开弹窗
    openPathDialog() {
      this.pathDialogVisible = true;
      // 弹窗打开后，由于 DOM 渲染顺序，initPathMap 会在 @opened 触发
    },

    // 初始化地图
    initPathMap() {
      if (!this.pathMap) {
        this.pathMap = new window.AMap.Map("path-map-container", {
          zoom: 13,
          center: [this.metrics.longitude || 103.95, this.metrics.latitude || 30.67]
        });
      }
      this.fetchPathData();
    },

    // 请求数据
    async fetchPathData() {
      this.pathLoading = true;
      try {
        const res = await sensorPathData(this.type, {
          date: this.pathDate,
          device_id: '2411220000001856'
        });
        this.pathPoints = res;
        this.drawPath();
      } catch (e) {
        this.$message.error("获取轨迹失败");
      } finally {
        this.pathLoading = false;
      }
    },

    // 绘制轨迹
    drawPath() {
      if (!this.pathMap) return;

      // 1. 清除旧的覆盖物
      if (this.pathPolyline) this.pathMap.remove(this.pathPolyline);
      if (this.startEndMarkers.length) this.pathMap.remove(this.startEndMarkers);
      this.startEndMarkers = [];

      if (this.pathPoints.length < 2) {
        this.$message.warning("该时段轨迹点不足以成线");
        return;
      }

      // 2. 转换坐标格式 [lng, lat]
      const path = this.pathPoints.map(p => [p.lng, p.lat]);

      // 3. 绘制折线
      this.pathPolyline = new window.AMap.Polyline({
        path: path,
        isOutline: true,
        outlineColor: '#ffeeff',
        borderWeight: 2,
        strokeColor: "#3366FF",
        strokeOpacity: 1,
        strokeWeight: 6,
        strokeStyle: "solid",
        lineJoin: 'round',
        lineCap: 'round',
      });

      this.pathMap.add(this.pathPolyline);

      // 4. 添加起点和终点
      const startMarker = new window.AMap.Marker({
        position: path[0],
        content: '<div class="marker-start">起</div>',
        offset: new window.AMap.Pixel(-10, -10)
      });
      const endMarker = new window.AMap.Marker({
        position: path[path.length - 1],
        content: '<div class="marker-end">终</div>',
        offset: new window.AMap.Pixel(-10, -10)
      });

      this.startEndMarkers = [startMarker, endMarker];
      this.pathMap.add(this.startEndMarkers);

      // 5. 自动调整视野
      this.pathMap.setFitView([this.pathPolyline]);
    },
    showVideoLogs() {
      if (this.metrics.video_logs.length < 1) {
        this.$message.error("当前时段没有视频")
        return
      }
      this.show_video_logs = true
    },
  }
}
</script>

<style scoped lang="scss">
.main-container {
  flex: 1;
  height: 100vh;
  background-color: #f0f2f5;

  :deep(.main-container-scrollbar-wrapper) {
    overflow-x: hidden !important;
  }

  :deep(.main-container-scrollbar-view) {
    min-height: 100vh;
  }
}

.monitor-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;

  .header-section {
    margin: -20px;
    background-color: #A40000;
    color: #FFFFFF;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .main-title {
      font-size: 18px;
      font-weight: bold;
      margin-left: 10px;
    }
  }

  .kpi-flex-container {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 15px; /* 卡片间距 */
    margin-bottom: 20px;

    .kpi-card-item {
      flex: 1; /* 均匀等分空间 */
      min-width: 200px; /* 保证卡片不会缩得太小 */
      border: none;
      border-left: 4px solid #ccc;
      transition: transform 0.3s;

      &:hover {
        transform: translateY(-3px);
      }

      .kpi-content {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .label {
          color: #909399;
          font-size: 13px;
          margin-bottom: 5px;

          .sub-name {
            color: #909399;
            font-size: 12px;
            margin-left: 5px;
          }
        }

        .value {
          font-size: 24px;
          font-weight: bold;
          color: #303133;

          small {
            font-size: 13px;
            margin-left: 3px;
            font-weight: normal;
          }
        }

        .kpi-icon {
          font-size: 32px;
          opacity: 0.3;
        }
      }
    }
  }

  .location-row {
    margin-bottom: 20px;

    .location-card {
      border: none;
      background: #fff;

      .location-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .loc-left, .loc-right {
          display: flex;
          align-items: center;
        }

        i.el-icon-location-information {
          font-size: 20px;
          color: #f56c6c;
          margin-right: 8px;
        }

        .loc-label {
          font-size: 14px;
          color: #909399;
          font-weight: 500;
        }

        .loc-address {
          font-size: 14px;
          color: #303133;
          font-weight: 500;
        }

        .geo-link {
          font-family: monospace;
          font-size: 15px;
          font-weight: bold;

          i {
            margin-left: 4px;
          }
        }
      }
    }
  }

  .chart-row {
    margin-bottom: 20px;

    .chart-box {
      height: 450px;
    }

    .chart-box-s {
      height: 300px;
    }
  }

  .mt-20 {
    margin-top: 20px;
  }
}

.path-dialog {
  .path-toolbar {
    margin-bottom: 15px;
    display: flex;
    align-items: center;

    .path-info {
      margin-left: 20px;
      font-size: 14px;
      color: #909399;
    }
  }

  #path-map-container {
    width: 100%;
    height: 60vh; // 弹窗中地图的高度
    border-radius: 4px;
    border: 1px solid #dcdfe6;
  }
}

/* 起终点样式 */
::v-deep {
  .marker-start, .marker-end {
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    border-radius: 50%;
    color: #fff;
    font-size: 12px;
    font-weight: bold;
  }

  .marker-start {
    background: #67C23A;
    box-shadow: 0 0 5px #67C23A;
  }

  .marker-end {
    background: #F56C6C;
    box-shadow: 0 0 5px #F56C6C;
  }
}

.video-list {
  .video-item {
    video, img {
      width: 100%;
      height: auto;
    }
  }

  .label {
    color: #666666;
    padding: 20px 0;
    text-align: center;
  }
}
</style>
